/**
 * Base2DVisualizer - Base class for 2D Canvas visualizers
 *
 * Provides common functionality:
 * - Canvas 2D context setup
 * - Configuration management with defaults
 * - Frequency data processing with smoothing
 * - Glow effects
 * - Color interpolation
 * - Enhanced audio features (optional, lazy-evaluated)
 *
 * Subclasses must implement:
 * - static name: string
 * - static displayName: string
 * - static getConfigUI(): ConfigItem[]
 * - getElementCount(): number
 * - renderFrame(amplitudes: number[], features?: AudioFeatures): void
 *
 * Subclasses may override:
 * - getDefaultConfig(): object
 * - init(): void
 * - onConfigChange(changed: object): void
 */

import { mapFrequencyData, smoothAmplitudes, lerpColor, extractConfigDefaults } from './audio-utils.js';
import { drawBackground } from '../app/background-controller.js';
import { applyAudioFeatureMixin } from './audio-feature-mixin.js';

export class Base2DVisualizer {
  static contextType = '2d';

  // Subclasses MUST override these
  static name = '';
  static displayName = '';

  /**
   * @param {HTMLCanvasElement} canvas - The canvas element to render to
   * @param {object} options - Configuration options
   */
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    // Extract defaults from getConfigUI() if available
    const uiDefaults = this.constructor.getConfigUI
      ? extractConfigDefaults(this.constructor.getConfigUI())
      : {};

    // Merge defaults with options
    // Order: base defaults -> UI defaults -> subclass defaults -> user options
    this.config = {
      colorStart: '#00ff88',
      colorEnd: '#ff0088',
      backgroundColor: '#0a0a0a',
      smoothing: 0.8,
      glow: true,
      ...uiDefaults,
      ...this.getDefaultConfig(),
      ...options
    };

    // State for amplitude smoothing
    this.previousAmplitudes = [];

    // Pre-allocated buffer for frequency mapping (avoids per-frame GC)
    this._freqBuffer = null;

    // Allow subclass initialization
    this.init();
  }

  /**
   * Returns subclass-specific default configuration.
   * Override in subclass to provide visualizer-specific defaults.
   * @returns {object}
   */
  getDefaultConfig() {
    return {};
  }

  /**
   * Called after constructor setup. Override in subclass for additional initialization.
   */
  init() {}

  /**
   * Called when configuration changes. Override to handle specific config updates.
   * @param {object} changed - Object containing only the changed keys and their new values
   */
  onConfigChange(changed) {}

  /**
   * Returns the number of visualization elements (e.g., bars, bubbles).
   * MUST be implemented by subclass.
   * @returns {number}
   */
  getElementCount() {
    throw new Error('Subclass must implement getElementCount()');
  }

  /**
   * Renders a single frame with the given smoothed amplitude values.
   * MUST be implemented by subclass.
   * @param {Array<number>} amplitudes - Smoothed amplitude values (0-1) for each element
   * @param {AudioFeatures} [features] - Optional enhanced audio features (lazy-evaluated)
   */
  renderFrame(amplitudes, features) {
    throw new Error('Subclass must implement renderFrame()');
  }

  /**
   * Main draw method called each frame with frequency data.
   * Handles frequency mapping, smoothing, canvas clearing, and delegates to renderFrame.
   * @param {Uint8Array} frequencyData - Frequency data from AnalyserNode (0-255 values)
   * @param {AudioFeatures} [features] - Optional enhanced audio features (lazy-evaluated)
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    // Ensure buffer is properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    // Map frequency data to element count
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    // Ensure previousAmplitudes is properly sized
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    // Apply smoothing
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Store features for convenience getters
    this._currentFeatures = features;

    // Clear canvas and render
    this.clearCanvas();
    this.renderFrame(amplitudes, features);
  }

  // Audio feature convenience getters are added via mixin (see end of file)

  /**
   * Clears the canvas with the configured background.
   * Uses the global background settings from background-controller.
   */
  clearCanvas() {
    drawBackground(this.ctx, this.canvas.width, this.canvas.height);
  }

  /**
   * Applies a glow effect to subsequent draw operations.
   * @param {string} color - Glow color
   * @param {number} intensity - Blur intensity (default: 15)
   */
  applyGlow(color, intensity = 15) {
    if (this.config.glow) {
      this.ctx.shadowBlur = intensity;
      this.ctx.shadowColor = color;
    }
  }

  /**
   * Resets glow effect after drawing.
   */
  resetGlow() {
    this.ctx.shadowBlur = 0;
  }

  /**
   * Updates configuration and notifies subclass of changes.
   * @param {object} newConfig - New configuration values to merge
   */
  setConfig(newConfig) {
    // Track what actually changed
    const changed = {};
    for (const key in newConfig) {
      if (this.config[key] !== newConfig[key]) {
        changed[key] = newConfig[key];
      }
    }

    // Merge new config
    this.config = { ...this.config, ...newConfig };

    // Notify subclass of changes
    if (Object.keys(changed).length > 0) {
      this.onConfigChange(changed);
    }
  }

  /**
   * Clears the canvas and resets smoothing state.
   */
  clear() {
    this.clearCanvas();
    this.previousAmplitudes.fill(0);
  }

  /**
   * Interpolates between the configured start and end colors.
   * @param {number} t - Interpolation factor (0-1)
   * @returns {string} Interpolated color in rgb() format
   */
  lerpColor(t) {
    return lerpColor(this.config.colorStart, this.config.colorEnd, t);
  }

  /**
   * Release references for garbage collection.
   * Subclasses should call super.dispose() and nullify their own resources.
   */
  dispose() {
    this.ctx = null;
    this.canvas = null;
    this._freqBuffer = null;
    this.previousAmplitudes = [];
    this._currentFeatures = null;
  }
}

// Apply audio feature convenience getters (beatDetected, bassEnergy, etc.)
applyAudioFeatureMixin(Base2DVisualizer);
